import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/lib/auth'
import { leadClaimDb } from '@/lib/leadClaimDb'
import { errorMessage } from '@/lib/errorMessage'
import { readClaimOutcome, type ClaimOutcome, type LeadOffer } from '@/lib/leadOffers'

// RIZ127 (mig 470) - First to Claim offers made to the signed-in member.
//
// Read ONLY through lead_claim_offers_for_me: an offered lead has no agent, and contacts RLS shows
// an agent only their own book, so the essentials of a lead on offer come from that function's
// allow-list (never the phone, email or the contact row). Claimed ONLY through claim_lead_offer,
// which is atomic: the first claim wins and every other one is told who got it.

export const LEAD_OFFERS_QUERY_KEY = 'lead-claim-offers'

export function useMyLeadOffers(opts: { offerIds?: string[] | null; enabled?: boolean } = {}): {
  offers: LeadOffer[]
  loading: boolean
  failed: boolean
  refetch: () => void
} {
  const { activeOrg } = useAuth()
  const orgId = activeOrg?.organization_id
  const ids = opts.offerIds ?? null
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [LEAD_OFFERS_QUERY_KEY, orgId, ids ? [...ids].sort().join(',') : 'mine'],
    queryFn: async () => {
      const { data: rows, error: readError } = await leadClaimDb.rpc('lead_claim_offers_for_me', {
        p_org_id: orgId!,
        p_offer_ids: ids,
      })
      if (readError) throw readError
      return (rows ?? []) as LeadOffer[]
    },
    enabled: !!orgId && (opts.enabled ?? true) && (ids === null || ids.length > 0),
    // A claim window is minutes long: keep what is on screen honest without a page reload.
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  return { offers: data ?? [], loading: isLoading, failed: !!error, refetch: () => { void refetch() } }
}

export async function claimLeadOffer(offerId: string): Promise<ClaimOutcome> {
  const { data, error } = await leadClaimDb.rpc('claim_lead_offer', { p_offer_id: offerId })
  if (error) throw error
  const outcome = readClaimOutcome(data)
  if (!outcome) throw new Error('The claim came back in a shape this page does not understand. Reload and try again.')
  return outcome
}

/**
 * Claim, with the busy flag and the refresh every surface needs afterwards. The caller decides
 * what a win does (open the lead); every other outcome comes back as a sentence to show on the
 * offer - a lost race is information, not an error.
 */
export function useClaimLeadOffer(): {
  claim: (offerId: string) => Promise<ClaimOutcome | null>
  busyId: string | null
  error: string | null
} {
  const queryClient = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const claim = useCallback(async (offerId: string) => {
    setBusyId(offerId)
    setError(null)
    try {
      return await claimLeadOffer(offerId)
    } catch (e: unknown) {
      setError(errorMessage(e, 'Could not claim this lead.'))
      return null
    } finally {
      setBusyId(null)
      void queryClient.invalidateQueries({ queryKey: [LEAD_OFFERS_QUERY_KEY] })
    }
  }, [queryClient])

  return { claim, busyId, error }
}
